import React, { useState } from 'react';
import axios from 'axios';
import { getApiErrorMessage } from '../../services/errorUtils';
import '../layout/DevPanel.css';
import './InvDiffReview.css';

const API_BASE = '/api/dev';

interface DiffRow {
  sku: string;
  supabase_qty: number | null;
  shopify_qty: number | null;
  delta: number;
}

const InvDiffReview: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [diffs, setDiffs] = useState<DiffRow[]>([]);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const handleCompare = async () => {
    try {
      setLoading(true);
      setError(null);
      setDiffs([]);
      setHasLoaded(false);
      const { data } = await axios.get(`${API_BASE}/inventory-diff`);
      setDiffs(data.diffs || []);
      setHasLoaded(true);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to compare inventory'));
      setDiffs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatQty = (val: number | null | undefined): string => (val === null || val === undefined ? '—' : String(val));

  return (
    <div className="dev-panel-container inv-diff-container">
      <h2>Inventory Diff Review</h2>
      <p className="inv-diff-description">
        Compare <strong>inventory_levels</strong> (Supabase) vs Shopify GraphQL <strong>on_hand</strong>. Only SKUs with different values are listed.
      </p>
      <div className="dev-panel-section">
        <button
          className="dev-panel-button inv-diff-compare-button"
          onClick={handleCompare}
          disabled={loading}
        >
          {loading ? 'Comparing...' : 'Compare Inventory Diff'}
        </button>
      </div>
      {error && <div className="dev-panel-error">{error}</div>}
      {diffs.length > 0 && (
        <div className="inv-diff-result">
          <p className="inv-diff-summary">{diffs.length} SKU{diffs.length !== 1 ? 's' : ''} with differences</p>
          <div className="inv-diff-table-wrap">
            <table className="dev-panel-backup-table inv-diff-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Supabase</th>
                  <th>Shopify</th>
                  <th>Delta</th>
                </tr>
              </thead>
              <tbody>
                {diffs.map((row) => (
                  <tr key={row.sku}>
                    <td className="inv-diff-sku">{row.sku}</td>
                    <td>{formatQty(row.supabase_qty)}</td>
                    <td>{formatQty(row.shopify_qty)}</td>
                    <td className={row.delta > 0 ? 'inv-diff-delta-positive' : row.delta < 0 ? 'inv-diff-delta-negative' : ''}>
                      {row.delta > 0 ? '+' : ''}{row.delta}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!loading && !error && hasLoaded && diffs.length === 0 && (
        <p className="inv-diff-empty">No differences found between Supabase and Shopify.</p>
      )}
      {!loading && !error && !hasLoaded && (
        <p className="inv-diff-empty">Click &quot;Compare Inventory Diff&quot; to load differences.</p>
      )}
    </div>
  );
};

export default InvDiffReview;
